import type { FieldConfig } from '@components/integrated/entity-form';
import { DEFAULT_REQUIRED_MESSAGE } from '@components/shared/configurable';

// ===========================================================================
// « Commentaire » field of the estimation cards (mahhp 22858) — shared by
// `EstimationCard` (« règlement ») and its twin `EstimationRecoursCard`
// (« recours »).
// ---------------------------------------------------------------------------
// A required comment, placed under the card total and above the submit button,
// that is sent with the card's save workflow (`estimationSaveService`,
// ITEM-20261103-2139-xido), which (1) forwards it to the `WS2_GS_VER_ESTIMATION`
// check as `commentaire` and (2) posts it on the claim once the estimation is
// saved. The field is a regular entry of each card's EntityForm config, so it
// is published to the layout editor and every layout rule (rename /
// placeholder / helper text / tooltip / hide) targets it by its stable key.
// ===========================================================================

/**
 * Stable field key — the rule contract AND the `commentaire` key of the
 * `WS2_GS_VER_ESTIMATION` query. DO NOT rename.
 */
export const FIELD_COMMENTAIRE = 'commentaire';

/**
 * The card fields rendered in the bottom group (between the total and the
 * submit button, per the mockups) rather than at the top of the card. Every
 * other config field stays in the top group. Never mutated.
 */
export const COMMENT_GROUP_FIELDS: string[] = [FIELD_COMMENTAIRE];

/**
 * The `commentaire` field config. Label, placeholder and the required message
 * live here (never inline JSX) so `renameField` / `setFieldPlaceholder` /
 * `hideField` rules can target them; `validation.required` makes it a
 * baseline-required field (asterisk + client validation on blur).
 *
 * @param order - sort order within the card config (after the card's other
 *   fields, so the published config lists it last).
 */
export function buildCommentaireFieldConfig(order: number): FieldConfig {
  return {
    type: 'textarea',
    label: 'Commentaire',
    placeholder: 'Précisez le contexte de cette estimation',
    order,
    validation: { required: 'Le commentaire est obligatoire.' },
    options: { rows: 3 },
  };
}

/** The error shown when the required comment is empty on submit. */
export function requiredMessageOf(field: FieldConfig | undefined): string {
  const required = field?.validation?.required;
  return typeof required === 'string' && required !== '' ? required : DEFAULT_REQUIRED_MESSAGE;
}

/** The comment as posted: the trimmed form value (`''` when absent). */
export function commentValue(raw: unknown): string {
  return typeof raw === 'string' ? raw.trim() : '';
}

/**
 * The submit error shown in the card's error `Alert`, rendered right above the
 * submit button. `title` is set only for a blocked VEOS check — KO or check
 * unavailable (« Contrôle VEOS », carried by `estimationSaveService.save`'s
 * result); the other submit errors (missing `cdUtil`, input / save failure,
 * comment not sent) have none.
 */
export interface EstimationSubmitError {
  title?: string;
  message: string;
}

/** Card-level messages of the comment flow. */
export const ESTIMATION_COMMENT_TEXT = {
  /** Same wording as `CommentsPanel`: the comment `user` is the ERP `CD_UTIL`. */
  missingCdUtil:
    "Aucun utilisateur ERP n'est associé à la session active — bascule sur la session via le sélecteur en haut, puis réessaye.",
  /**
   * The estimation was written but the comment step failed. The text stays in
   * the field so the handler can copy it into the Commentaires panel rather
   * than re-submitting (which would write the estimation a second time).
   */
  commentNotSent: (savedSentence: string, detail: string) =>
    `${savedSentence}, mais le commentaire n’a pas pu être envoyé (${detail}). Copiez-le et publiez-le depuis le panneau Commentaires.`,
} as const;
